import { extractErrorInfo } from '@src/utility/error';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LogManager } from '../utility/Logger';
import { useFlushOnLeave } from './useFlushOnLeave';

const logger = LogManager.getLogger('feature:autosave');

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type UseAutosaveOptions = {
  /**
   * Async save function the caller provides. Receives the current content.
   * Should resolve when the persistence round-trip is complete.
   */
  save: (content: string) => Promise<void>;
  /** Idle debounce in ms before firing a save after an edit. Default: 2000ms. */
  debounceMs?: number;
  /** Disable autosave entirely (e.g., while an AI generation is in progress). */
  enabled?: boolean;
};

type UseAutosaveReturn = {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  /** Notify the autosave hook that content has changed. Schedules a debounced save. */
  onContentChange: (content: string) => void;
  /** Flush any pending save immediately (returns a promise that resolves when done). */
  flush: () => Promise<void>;
  /**
   * Clear the hook's internal dedup state. Call this when switching to a
   * different content entry (e.g., selecting a different article) so that
   * the `lastSavedContentRef` from the previous entry doesn't mask a
   * legitimate change on the new entry.
   */
  reset: () => void;
};

/**
 * Idle-based autosave hook. Debounces saves on edits, flushes on blur /
 * beforeunload / tab-hidden, exposes a status + lastSavedAt for ambient UI.
 *
 * Intentionally does NOT own the content — the caller passes the latest content
 * into `onContentChange`. This keeps the hook reusable across editors.
 */
export function useAutosave(options: UseAutosaveOptions): UseAutosaveReturn {
  const { save, debounceMs = 2000, enabled = true } = options;

  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Refs so event handlers and timers always see the latest values without re-binding.
  const saveRef = useRef(save);
  saveRef.current = save;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const pendingRef = useRef<string | null>(null);
  const lastSavedContentRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  // `debouncedSave` wraps `runSave` via a ref indirection to break the
  // mutual dependency: runSave may re-enqueue itself (post-save, when more
  // edits landed mid-save) through debouncedSave. A direct closure would
  // require runSave to exist at the moment debouncedSave is created.
  const runSaveRef = useRef<() => Promise<void>>();

  const debouncedSave = useMemo(
    () => debounce(() => runSaveRef.current?.(), debounceMs),
    [debounceMs],
  );

  const runSave = useCallback(async () => {
    const content = pendingRef.current;
    if (content === null) return;
    if (content === lastSavedContentRef.current) {
      pendingRef.current = null;
      return;
    }
    if (inFlightRef.current) {
      // Another save is in flight — leave the pending ref set so the
      // post-save re-enqueue below picks up the latest content.
      return;
    }

    inFlightRef.current = true;
    setStatus('saving');

    try {
      await saveRef.current(content);
      lastSavedContentRef.current = content;
      setLastSavedAt(new Date());
      setStatus('saved');
      // If more edits landed during the save, enqueue another pass so the
      // newer content doesn't get stranded by the in-flight guard above.
      if (
        pendingRef.current !== null &&
        pendingRef.current !== lastSavedContentRef.current
      ) {
        // Microtask so React state settles first.
        Promise.resolve().then(() => debouncedSave());
      } else {
        pendingRef.current = null;
      }
    } catch (err) {
      logger.error('Autosave failed', extractErrorInfo(err));
      setStatus('error');
    } finally {
      inFlightRef.current = false;
    }
  }, [debouncedSave]);

  runSaveRef.current = runSave;

  const onContentChange = useCallback(
    (content: string) => {
      if (!enabledRef.current) return;
      // No-op if identical to last successfully saved content.
      if (content === lastSavedContentRef.current) return;

      pendingRef.current = content;
      setStatus('idle');
      debouncedSave();
    },
    [debouncedSave],
  );

  const flush = useCallback(async () => {
    debouncedSave.cancel();
    await runSave();
  }, [debouncedSave, runSave]);

  const reset = useCallback(() => {
    debouncedSave.cancel();
    pendingRef.current = null;
    lastSavedContentRef.current = null;
    // Deliberately do NOT reset `lastSavedAt` — the "Saved · N min ago" label
    // from a prior entry would be wrong for the new entry, but it's no worse
    // than any other stale UI and clearing it causes flicker. Callers that
    // want a clean slate can re-render with a new key.
    setStatus('idle');
  }, [debouncedSave]);

  // Safety saves: blur / visibility hidden / beforeunload.
  useFlushOnLeave(flush, () => pendingRef.current !== null);

  // Cancel any queued debounced save on unmount so it doesn't fire after
  // the hook's owning component is gone.
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return {
    status,
    lastSavedAt,
    onContentChange,
    flush,
    reset,
  };
}
